import React, { useState } from "react";
import { Modal, Form, Input, Button, Alert } from "antd";
import { UserOutlined, MailOutlined, BankOutlined, PhoneOutlined } from "@ant-design/icons";
import { registerGuest } from "api/issue/issueApi";

// Issue icon for modal header
const IssueIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#3B82F6"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 19l7-7 3 3-7 7-3-3z" />
    <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
    <path d="M2 2l7.586 7.586" />
    <circle cx="11" cy="11" r="2" />
  </svg>
);

export default function GuestRegistrationModal({
  visible,
  shareToken,
  issueTitle,
  onSuccess,
  onCancel,
  required = false,
  // Someone with an account on a public link: sign in instead of joining
  // as a guest (IssueResolutionPage). No prop, no link.
  onSignIn = null,
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Prefill from link hints (?name=&email=) so known people only confirm.
  const hints = (() => { try { const q = new URLSearchParams(window.location.search); return { name: q.get("name") || "", email: q.get("email") || "" }; } catch { return { name: "", email: "" }; } })();

  const handleSubmit = async (values) => {
    setLoading(true);
    setError(null);

    try {
      const guestData = {
        name: values.name.trim(),
        email: values.email.trim().toLowerCase(),
        company: values.company?.trim() || "",
        phone: values.phone?.trim() || "",
      };

      const response = await registerGuest(shareToken, guestData);
      
      form.resetFields();
      onSuccess(response.guest);
    } catch (err) {
      console.error("Guest registration failed:", err);
      setError(err.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (!required) {
      form.resetFields();
      setError(null);
      onCancel();
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <IssueIcon />
          <span style={{ fontSize: 18, fontWeight: 600 }}>Join Issue Session</span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      closable={!required}
      maskClosable={!required}
      centered
      width={440}
      destroyOnClose
    >
      <div style={{ padding: "8px 0" }}>
        {issueTitle && (
          <div
            style={{
              background: "#F0F9FF",
              border: "1px solid #BAE6FD",
              borderRadius: 8,
              padding: "12px 16px",
              marginBottom: 16,
            }}
          >
            <p style={{ margin: 0, fontSize: 14, color: "#0369A1" }}>
              You're joining: <strong style={{ color: "#0C4A6E" }}>{issueTitle}</strong>
            </p>
          </div>
        )}

        <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
          Please provide your information to collaborate on this issue. Your
          changes will be tracked and attributed to you.
        </p>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark="optional"
          initialValues={{ name: hints.name, email: hints.email }}
        >
          <Form.Item
            name="name"
            label="Full Name"
            rules={[
              { required: true, message: "Please enter your name" },
              { min: 2, message: "Name must be at least 2 characters" },
              { max: 100, message: "Name is too long" },
            ]}
          >
            <Input
              prefix={<UserOutlined style={{ color: "#9CA3AF" }} />}
              placeholder="John Doe"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: "Please enter your email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input
              prefix={<MailOutlined style={{ color: "#9CA3AF" }} />}
              placeholder="john@company.com"
              size="large"
              type="email"
            />
          </Form.Item>

          <Form.Item
            name="company"
            label="Company / Organization"
            rules={[{ max: 100, message: "Company name is too long" }]}
          >
            <Input
              prefix={<BankOutlined style={{ color: "#9CA3AF" }} />}
              placeholder="Acme Inc. (optional)"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone Number"
            rules={[
              {
                pattern: /^[+]?[\d\s\-()]+$/,
                message: "Please enter a valid phone number",
              },
            ]}
          >
            <Input
              prefix={<PhoneOutlined style={{ color: "#9CA3AF" }} />}
              placeholder="+1 (555) 000-0000 (optional)"
              size="large"
            />
          </Form.Item>

          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "flex-end",
              marginTop: 24,
              paddingTop: 16,
              borderTop: "1px solid #E5E7EB",
            }}
          >
            {!required && (
              <Button onClick={handleCancel} size="large">
                Cancel
              </Button>
            )}
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              size="large"
              block={required}
            >
              {loading ? "Joining..." : "Join Session"}
            </Button>
          </div>
        </Form>

        {onSignIn && (
          <div style={{ textAlign: "center", marginTop: 12, fontSize: 13, color: "#6B7280" }}>
            Have a BidLight account?{" "}
            <Button type="link" size="small" style={{ padding: 0 }} onClick={onSignIn}>
              Sign in instead
            </Button>
          </div>
        )}

        <p
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12,
            color: "#9CA3AF",
            marginTop: 16,
            paddingTop: 16,
            borderTop: "1px solid #F3F4F6",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          Your information is used only for attribution and will not be shared
          with third parties.
        </p>
      </div>
    </Modal>
  );
}
